import Axios from 'axios'
import { ChipOwnProps } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import moment from 'moment'
import API from '../API'

export type BoardguardTicketStatus = 'PENDING' | 'OK' | 'ATTENTION' | 'CANCELLED' | 'ERROR'

export type BoardguardPdfLang = 'pt' | 'en'

export const BOARDGUARD_PDF_LANG_OPTIONS: Array<{ value: BoardguardPdfLang; label: string }> = [
    { value: 'pt', label: 'Português' },
    { value: 'en', label: 'Inglês' },
]

export const boardguardPdfFilename = (pnr: string, lang: BoardguardPdfLang = 'pt') =>
    `${lang === 'en' ? 'ticket' : 'bilhete'}-${pnr}.pdf`

export interface BoardguardChange {
    type: string
    severity: 'info' | 'warning' | 'critical'
    segment: string | null
    flightNumber: string | null
    title: string
    description: string
    before: string | null
    after: string | null
}

export interface BoardguardNoShowSegment {
    segment: string | null
    flightNumber: string | null
    departure: string | null
    passengers: string[]
}

export interface BoardguardTicket {
    id: number
    order_id: number
    ticket_id: string
    status: BoardguardTicketStatus
    archived: boolean
    pdf_ready: boolean
    last_checked_at: string | null
    next_check_at: string | null
    last_error: string | null
    last_changes: BoardguardChange[] | null
    no_show_segments: BoardguardNoShowSegment[] | null
    updated_at: string
}

export interface NoShowFields {
    no_show?: boolean
    no_show_source?: 'BOARDGUARD' | 'MANUAL' | null
    no_show_at?: string | null
}

export const pickNoShowFields = (fields: NoShowFields): NoShowFields => ({
    no_show: fields.no_show,
    no_show_source: fields.no_show_source,
    no_show_at: fields.no_show_at,
})

export const formatNoShowSegment = (segment: BoardguardNoShowSegment) => {
    const route = [segment.flightNumber, segment.segment].filter(Boolean).join(' · ') || 'Trecho não identificado'
    const departure = segment.departure ? ` em ${moment(segment.departure).format('DD/MM/YYYY HH:mm')}` : ''
    const passengers = segment.passengers.length ? ` (${segment.passengers.join(', ')})` : ''
    return `${route}${departure}${passengers}`
}

export const getNoShowTooltip = (fields: NoShowFields, ticket?: BoardguardTicket | null) => {
    if (!fields.no_show) return ''
    const source = fields.no_show_source === 'MANUAL' ? 'Marcado manualmente' : 'Capturado pelo Boardguard'
    const parts = [fields.no_show_at ? `${source} em ${formatBoardguardDate(fields.no_show_at)}` : source]
    for (const segment of ticket?.no_show_segments ?? []) parts.push(formatNoShowSegment(segment))
    return parts.join(' | ')
}

export type BoardguardDisplayStatus = BoardguardTicketStatus | 'NOT_MONITORED' | 'ARCHIVED'

export const BOARDGUARD_STATUS_OPTIONS: Array<{ value: BoardguardDisplayStatus, label: string }> = [
    { value: 'NOT_MONITORED', label: 'Não monitorado' },
    { value: 'PENDING', label: 'Verificando' },
    { value: 'OK', label: 'Em dia' },
    { value: 'ATTENTION', label: 'Atenção' },
    { value: 'CANCELLED', label: 'Cancelado' },
    { value: 'ERROR', label: 'Erro' },
    { value: 'ARCHIVED', label: 'Arquivado' },
]

export const getBoardguardDisplayStatus = (ticket?: BoardguardTicket | null): BoardguardDisplayStatus => {
    if (!ticket) return 'NOT_MONITORED'
    if (ticket.archived) return 'ARCHIVED'
    return ticket.status
}

export const getBoardguardStatusLabel = (status: BoardguardDisplayStatus) =>
    BOARDGUARD_STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status

export const getBoardguardStatusColor = (status: BoardguardDisplayStatus): ChipOwnProps['color'] => {
    switch (status) {
        case 'OK':
            return 'success'
        case 'PENDING':
            return 'info'
        case 'ATTENTION':
            return 'warning'
        case 'CANCELLED':
        case 'ERROR':
            return 'error'
        default:
            return 'default'
    }
}

export const formatBoardguardDate = (value?: string | null) =>
    value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'

export const getBoardguardTooltip = (ticket?: BoardguardTicket | null) => {
    if (!ticket) return ''
    const parts = [`Última checagem: ${formatBoardguardDate(ticket.last_checked_at)}`]
    if (ticket.last_error) parts.push(`Erro: ${ticket.last_error}`)
    return parts.join(' | ')
}

const getErrorMessage = (error: unknown, fallback: string) => {
    if (Axios.isAxiosError(error)) {
        return (error.response?.data as { message?: string } | undefined)?.message || fallback
    }
    return fallback
}

const readBlobMessage = async (data: unknown, fallback: string) => {
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text()) as { message?: string }
            return parsed.message || fallback
        } catch {
            return fallback
        }
    }
    return fallback
}

/**
 * The global interceptor resolves 403/409 responses with the AxiosError itself
 * (after showing a snackbar), so a resolved value is not necessarily a success.
 */
const postBoardguard = async <T,>(url: string, fallback: string): Promise<T | null> => {
    try {
        const response = await API.post<T>(url)
        if (Axios.isAxiosError(response)) return null
        return response.data
    } catch (error) {
        enqueueSnackbar(getErrorMessage(error, fallback), { variant: 'error' })
        return null
    }
}

export const monitorOrderOnBoardguard = async (orderId: number) => {
    const ticket = await postBoardguard<BoardguardTicket>(
        `/order/${orderId}/boardguard`,
        'Não foi possível monitorar a emissão no Boardguard.'
    )

    if (ticket) {
        enqueueSnackbar(
            ticket.pdf_ready
                ? 'Emissão vinculada ao Boardguard. O bilhete já está disponível para download.'
                : 'Monitoramento iniciado! Você será avisado quando o bilhete estiver pronto para download (cerca de 20 segundos).',
            { variant: 'success', autoHideDuration: 6000 }
        )
    }

    return ticket
}

export const triggerBoardguardCheck = async (orderId: number) => {
    const result = await postBoardguard<unknown>(
        `/order/${orderId}/boardguard/check`,
        'Não foi possível solicitar a verificação.'
    )

    if (result !== null) {
        enqueueSnackbar('Verificação solicitada. O status será atualizado em instantes.', { variant: 'info' })
    }

    return result !== null
}

export const setOrderNoShow = async (orderId: number, noShow: boolean): Promise<NoShowFields | null> => {
    try {
        const response = await API.put<NoShowFields>(`/order/${orderId}/no-show`, { no_show: noShow })
        if (Axios.isAxiosError(response)) return null
        enqueueSnackbar(noShow ? 'Emissão marcada como no-show.' : 'No-show desmarcado.', { variant: 'success' })
        return response.data
    } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'Não foi possível atualizar o no-show.'), { variant: 'error' })
        return null
    }
}

export const downloadTicketPdf = async (
    orderId: number,
    reservationCode?: string | null,
    lang: BoardguardPdfLang = 'pt',
) => {
    const fallback = 'Não foi possível baixar o PDF.'

    try {
        const response = await API.get<Blob>(`/order/${orderId}/boardguard/pdf`, {
            params: { lang },
            responseType: 'blob',
            validateStatus: () => true,
        })

        if (response.status < 200 || response.status >= 300) {
            enqueueSnackbar(await readBlobMessage(response.data, fallback), { variant: 'error' })
            return
        }

        const pnr = reservationCode?.replace(/\s+/g, '').toUpperCase() || `emissao-${orderId}`
        const url = URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.download = boardguardPdfFilename(pnr, lang)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch {
        enqueueSnackbar(fallback, { variant: 'error' })
    }
}
